// Text-template debug harness (Jun 30) — DEV-only.
//
// Groups a brand's text-overlay creatives by the text template they used, so
// the curator can see EVERY render of one template side-by-side and spot where
// its color/placement fails vs succeeds across the scenes it landed on. Each
// tile flips between the baked composite and the text-free base scene (what the
// template had to work against). Reads /api/dev/creatives-by-template
// (auth-skiplisted; prod DB through the local cloud-sql-proxy). Local debug
// tool — not intended for prod.

import { useCallback, useEffect, useMemo, useState } from 'react';
import { API_BASE } from '@/lib/api';

interface CreativeItem {
  post_id: string;
  template_id: string;
  image_url: string | null;
  base_image_url: string | null;
}
interface TemplateBucket {
  template_id: string;
  count: number;
}
interface Resp {
  brand_id: string;
  total: number;
  templates: TemplateBucket[];
  items: CreativeItem[];
}

const DEFAULT_BRAND = 'a6939224-d47a-421c-8c85-59ecfd8d5575';

export default function TemplateDebugPage() {
  const [brandInput, setBrandInput] = useState(DEFAULT_BRAND);
  const [brandId, setBrandId] = useState(DEFAULT_BRAND);
  const [data, setData] = useState<Resp | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [selected, setSelected] = useState<string | null>(null);
  const [flipped, setFlipped] = useState<Set<string>>(new Set());
  const [flipAll, setFlipAll] = useState(false);
  const [rebaking, setRebaking] = useState(false);
  const [rebakeMsg, setRebakeMsg] = useState<string | null>(null);

  const load = useCallback(async (bid: string, keep = false) => {
    setLoading(true);
    setError(null);
    if (!keep) {
      setData(null);
      setSelected(null);
      setFlipped(new Set());
    }
    try {
      const res = await fetch(
        `${API_BASE}/api/dev/creatives-by-template?brand_id=${encodeURIComponent(bid)}`,
        { cache: 'no-store' },
      );
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const json: Resp = await res.json();
      setData(json);
      if (keep) {
        // Refresh-in-place (after a re-bake): keep the current template selected
        // if it still exists, so the grid updates without jumping to another.
        setSelected((prev) =>
          prev && json.templates.some((t) => t.template_id === prev)
            ? prev
            : json.templates[0]?.template_id ?? null,
        );
      } else {
        setSelected(json.templates[0]?.template_id ?? null);
      }
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load(brandId);
  }, [brandId, load]);

  const handleRebake = useCallback(async () => {
    if (!selected || !data || rebaking) return;
    const count = data.templates.find((t) => t.template_id === selected)?.count ?? 0;
    if (
      !window.confirm(
        `Re-bake all ${count} render${count === 1 ? '' : 's'} of "${selected}" with the current template? ` +
          `Copy is preserved; only layout/font/color recompiles. This replaces their images ` +
          `(old ones stay in storage, recoverable). ~${Math.max(1, Math.round((count * 2.5) / 5) * 5)}s.`,
      )
    ) {
      return;
    }
    setRebaking(true);
    setRebakeMsg(`Re-baking ${count}…`);
    try {
      const res = await fetch(`${API_BASE}/api/dev/rebake-template`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ brand_id: brandId, template_id: selected }),
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const j = await res.json();
      setRebakeMsg(
        `Re-baked ${j.rebaked}${j.failed ? `, ${j.failed} failed` : ''}${
          j.skipped ? `, ${j.skipped} skipped` : ''
        }`,
      );
      await load(brandId, true);
    } catch (e) {
      setRebakeMsg(`Error: ${e instanceof Error ? e.message : String(e)}`);
    } finally {
      setRebaking(false);
    }
  }, [selected, data, rebaking, brandId, load]);

  const shown = useMemo(() => {
    if (!data) return [];
    if (!selected) return data.items;
    return data.items.filter((it) => it.template_id === selected);
  }, [data, selected]);

  const toggleFlip = useCallback((id: string) => {
    setFlipped((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }, []);

  const isFlipped = (id: string) => flipAll || flipped.has(id);

  if (!import.meta.env.DEV) {
    return <div className="p-8 text-sm">Dev only.</div>;
  }

  return (
    <div className="min-h-screen bg-neutral-50 text-neutral-900 flex">
      <aside className="w-64 shrink-0 border-r border-neutral-200 bg-white flex flex-col h-screen sticky top-0">
        <div className="p-4 border-b border-neutral-200">
          <div className="text-sm font-bold">Text-template debug</div>
          <div className="text-xs text-neutral-500 mt-0.5">Renders grouped by template</div>
          <input
            value={brandInput}
            onChange={(e) => setBrandInput(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') setBrandId(brandInput.trim());
            }}
            placeholder="brand_id"
            className="mt-3 w-full text-xs px-2 py-1.5 border border-neutral-300 rounded font-mono"
          />
          <button
            onClick={() => setBrandId(brandInput.trim())}
            className="mt-2 w-full text-xs font-semibold bg-neutral-900 text-white rounded py-1.5"
          >
            Load
          </button>
        </div>
        <div className="flex-1 overflow-y-auto p-2">
          {data ? (
            <>
              <button
                onClick={() => setSelected(null)}
                className={`w-full text-left px-2 py-1.5 rounded text-xs flex justify-between ${
                  selected === null
                    ? 'bg-pink-100 text-pink-700 font-semibold'
                    : 'hover:bg-neutral-100'
                }`}
              >
                <span>All templates</span>
                <span className="tabular-nums text-neutral-400">{data.total}</span>
              </button>
              {data.templates.map((t) => (
                <button
                  key={t.template_id}
                  onClick={() => setSelected(t.template_id)}
                  className={`w-full text-left px-2 py-1.5 rounded text-xs flex justify-between ${
                    selected === t.template_id
                      ? 'bg-pink-100 text-pink-700 font-semibold'
                      : 'hover:bg-neutral-100'
                  }`}
                >
                  <span className="font-mono truncate">{t.template_id}</span>
                  <span className="tabular-nums text-neutral-400 ml-2">{t.count}</span>
                </button>
              ))}
            </>
          ) : (
            <div className="text-xs text-neutral-400 p-2">
              {loading ? 'Loading…' : error ? `Error: ${error}` : 'No data'}
            </div>
          )}
        </div>
      </aside>

      <main className="flex-1 p-6">
        <div className="flex items-center justify-between mb-4 gap-4">
          <div className="min-w-0">
            <h1 className="text-lg font-bold truncate">{selected ?? 'All templates'}</h1>
            <p className="text-xs text-neutral-500">
              {shown.length} render{shown.length === 1 ? '' : 's'}
              {selected ? ` using ${selected}` : ''}
              {rebakeMsg ? ` · ${rebakeMsg}` : ''}
            </p>
          </div>
          <div className="flex items-center gap-4 shrink-0">
            <label className="flex items-center gap-2 text-xs font-semibold cursor-pointer select-none">
              <input
                type="checkbox"
                checked={flipAll}
                onChange={(e) => setFlipAll(e.target.checked)}
              />
              Show base scenes (all)
            </label>
            {selected ? (
              <button
                onClick={handleRebake}
                disabled={rebaking || shown.length === 0}
                title="Re-bake every render of this template with the current template definition (copy preserved)"
                className="text-xs font-bold px-4 py-2 rounded-full bg-pink-600 text-white hover:bg-pink-700 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {rebaking ? 'Re-baking…' : 'Update batch'}
              </button>
            ) : null}
          </div>
        </div>

        {loading ? (
          <div className="text-sm text-neutral-400">Loading…</div>
        ) : error ? (
          <div className="text-sm text-red-600">Error: {error}</div>
        ) : (
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 xl:grid-cols-5 gap-3">
            {shown.map((it) => {
              const showBase = isFlipped(it.post_id) && !!it.base_image_url;
              const src = showBase ? it.base_image_url : it.image_url || it.base_image_url;
              return (
                <div
                  key={it.post_id}
                  className="relative group rounded-lg overflow-hidden border border-neutral-200 bg-white"
                >
                  <a
                    href={`/creatives/${it.post_id}`}
                    target="_blank"
                    rel="noreferrer"
                    className="block aspect-square bg-neutral-100"
                  >
                    {src ? (
                      <img
                        src={src}
                        alt={it.template_id}
                        loading="lazy"
                        className="w-full h-full object-contain"
                      />
                    ) : (
                      <div className="w-full h-full grid place-items-center text-xs text-neutral-400">
                        no image
                      </div>
                    )}
                  </a>
                  {it.base_image_url ? (
                    <button
                      onClick={() => toggleFlip(it.post_id)}
                      title={showBase ? 'Show baked composite' : 'Show text-free scene'}
                      className="absolute top-2 right-2 z-10 px-2 py-1 rounded-full bg-white/85 hover:bg-neutral-900 hover:text-white text-xs font-bold ring-1 ring-neutral-300 backdrop-blur-sm"
                    >
                      {showBase ? 'scene' : 'baked'}
                    </button>
                  ) : null}
                </div>
              );
            })}
          </div>
        )}
      </main>
    </div>
  );
}
